// AI Takeoff Review — final approval screen: rate matching, smart trench
// build-ups, grouped totals, then BUILD TAKEOFF.
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, ArrowLeft, Check, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtQty, nzd } from "@/lib/takeoffGeo";
import { DISCIPLINE_COLORS } from "@/components/TakeoffAIPanel";

const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const parseDN = (s) => { const m = /(\d{2,4})/.exec(s || ""); return m ? Number(m[1]) : null; };

const trenchCalc = (L, W, D, bedMM, dn) => {
  const exc = L * W * D;
  const bed = L * W * (bedMM / 1000);
  const disp = dn ? Math.PI * Math.pow(dn / 2000, 2) * L : 0;
  return { exc: r2(exc), bed: r2(bed), backfill: r2(Math.max(exc - bed - disp, 0)) };
};

export default function TakeoffAIReview({ open, onClose, takeoffId, findings, onBuild }) {
  const eligible = useMemo(() => findings.filter((f) => ["accepted", "edited"].includes(f.status) && !f.built && f.qty != null), [findings]);
  const excluded = useMemo(() => findings.filter((f) => ["accepted", "edited"].includes(f.status) && !f.built && f.qty == null), [findings]);
  const [matches, setMatches] = useState(null); // {fid: {rate_id, description, unit_price, confidence}}
  const [opts, setOpts] = useState({}); // fid -> {useRate, trenchOn, W, D, bed}

  useEffect(() => {
    if (!open) return;
    setMatches(null);
    const init = {};
    eligible.forEach((f) => {
      init[f.fid] = {
        useRate: false,
        trenchOn: false,
        W: f.trench?.width_m ?? 0.9,
        D: f.depth_m ?? null,
        bed: f.trench?.bedding_mm ?? 150,
      };
    });
    setOpts(init);
    (async () => {
      try {
        const { data } = await api.post(`/takeoffs/${takeoffId}/ai-match-rates`, {
          items: eligible.map((f) => ({ fid: f.fid, name: f.name, unit: f.unit, size: f.size, material: f.material, type: f.type })),
        });
        setMatches(data.matches || {});
        setOpts((prev) => {
          const next = { ...prev };
          Object.entries(data.matches || {}).forEach(([fid, m]) => {
            if (m.confidence === "high" && next[fid]) next[fid] = { ...next[fid], useRate: true };
          });
          return next;
        });
      } catch { setMatches({}); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setOpt = (fid, changes) => setOpts((p) => ({ ...p, [fid]: { ...p[fid], ...changes } }));

  const groups = useMemo(() => {
    const by = {};
    eligible.forEach((f) => { (by[f.discipline || "Other"] = by[f.discipline || "Other"] || []).push(f); });
    return Object.entries(by).sort((a, b) => b[1].length - a[1].length);
  }, [eligible]);

  const ratesUsed = Object.entries(opts).filter(([fid, o]) => o.useRate && matches?.[fid]).length;
  const noRate = eligible.length - Object.keys(matches || {}).length;
  const trenchCount = Object.values(opts).filter((o) => o.trenchOn).length;
  const adminMins = Math.round(eligible.length * 2 + eligible.filter((f) => f.qty != null).length * 1.5 + ratesUsed * 1);

  const build = () => {
    const builds = eligible.map((f) => {
      const o = opts[f.fid] || {};
      const m = o.useRate ? matches?.[f.fid] : null;
      let trenchItems = null;
      if (o.trenchOn && f.type === "pipe" && o.D) {
        const c = trenchCalc(f.qty, o.W || 0.9, o.D, o.bed || 150, parseDN(f.size));
        trenchItems = [
          { name: `${f.name} — Trench excavation`, qty: c.exc, notes: `AI build-up: ${f.qty}m × ${o.W}m × ${o.D}m` },
          { name: `${f.name} — Bedding`, qty: c.bed, notes: `${o.bed}mm bedding${f.trench?.surround ? ` (${f.trench.surround})` : ""}` },
          { name: `${f.name} — Backfill`, qty: c.backfill, notes: "Excavation less bedding & pipe displacement" },
        ];
      }
      return { f, unitRate: m?.unit_price ?? null, rateDesc: m?.description ?? null, trenchItems };
    });
    onBuild(builds);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-zinc-100 dark:bg-[#0B0F14]" data-testid="ai-review-screen">
      {/* header */}
      <div className="shrink-0 px-4 lg:px-6 py-3 border-b border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] flex items-center gap-3">
        <button onClick={onClose} className="text-zinc-400 hover:text-[#FF5F15]" data-testid="ai-review-back"><ArrowLeft className="w-4.5 h-4.5" /></button>
        <div>
          <h2 className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FF5F15]" /> AI Takeoff Review
          </h2>
          <p className="text-[11px] text-zinc-500">Review quantities before adding them to your takeoff. Nothing is committed until you build.</p>
        </div>
        <div className="flex-1" />
        <div className="hidden md:flex items-center gap-4 text-[11px]" data-testid="ai-review-summary">
          <span><b className="font-mono text-[#FF5F15]">{eligible.length}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">ready</span></span>
          <span><b className="font-mono text-amber-500">{excluded.length}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">need qty</span></span>
          <span><b className="font-mono text-emerald-500">{matches ? `${Object.keys(matches).length}/${eligible.length}` : "…"}</b> <span className="text-zinc-500 uppercase text-[9px] font-bold">rate matched</span></span>
          <span className="flex items-center gap-1 text-zinc-500"><Clock className="w-3 h-3" /> ~{Math.floor(adminMins / 60) ? `${Math.floor(adminMins / 60)}h ` : ""}{adminMins % 60}m admin saved</span>
        </div>
      </div>

      {/* body */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 lg:px-6 py-4 space-y-4 max-w-5xl w-full mx-auto">
        {matches === null && (
          <p className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Matching your rate library…</p>
        )}
        {noRate > 0 && matches !== null && (
          <div className="px-3 py-2 border border-amber-500/40 bg-amber-500/5 text-[11px] text-amber-500 font-bold uppercase tracking-wider" data-testid="ai-review-norate">
            ⚠ {noRate} item{noRate === 1 ? " has" : "s have"} no rate match — they'll be added unpriced (set rates in the panel later)
          </div>
        )}
        {groups.map(([disc, fs]) => (
          <div key={disc} className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: DISCIPLINE_COLORS[disc] || "#71717a" }} />
              {disc} <span className="font-mono">{fs.length}</span>
            </div>
            <div className="divide-y divide-zinc-100 dark:divide-white/5">
              {fs.map((f) => {
                const o = opts[f.fid] || {};
                const m = matches?.[f.fid];
                const canTrench = f.type === "pipe";
                const c = o.trenchOn && o.D ? trenchCalc(f.qty, o.W || 0.9, o.D, o.bed || 150, parseDN(f.size)) : null;
                return (
                  <div key={f.fid} className="px-3 py-2.5" data-testid={`ai-review-item-${f.fid}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-semibold flex-1 min-w-[160px]">{f.name}</span>
                      <span className="font-mono text-xs text-[#FF5F15] font-bold">{fmtQty(f.qty)} {f.unit}</span>
                    </div>
                    {/* rate match */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {m ? (
                        <>
                          <span className="text-[10px] text-zinc-500">Rate match: <b className="text-zinc-700 dark:text-zinc-300">{m.description}</b> — <span className="font-mono">{nzd(m.unit_price)}/{m.unit || f.unit}</span>
                            <span className={`ml-1 px-1 text-[8px] font-bold uppercase border ${m.confidence === "high" ? "text-emerald-500 border-emerald-500/40" : "text-amber-500 border-amber-500/40"}`}>{m.confidence}</span>
                          </span>
                          {o.useRate ? (
                            <button onClick={() => setOpt(f.fid, { useRate: false })} className="flex items-center gap-0.5 text-[9px] font-bold uppercase text-emerald-500" data-testid={`ai-rate-toggle-${f.fid}`}>
                              <Check className="w-3 h-3" /> Using rate
                            </button>
                          ) : (
                            <button onClick={() => setOpt(f.fid, { useRate: true })} className="flex items-center gap-0.5 text-[9px] font-bold uppercase text-zinc-500 hover:text-[#FF5F15]" data-testid={`ai-rate-toggle-${f.fid}`}>
                              <XCircle className="w-3 h-3" /> Use rate
                            </button>
                          )}
                        </>
                      ) : matches !== null && (
                        <span className="text-[10px] text-zinc-500">No rate match — will be added unpriced</span>
                      )}
                    </div>
                    {/* smart trench */}
                    {canTrench && (
                      <div className="mt-1.5">
                        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 cursor-pointer">
                          <input type="checkbox" checked={!!o.trenchOn} onChange={(e) => setOpt(f.fid, { trenchOn: e.target.checked })}
                            className="accent-[#FF5F15]" data-testid={`ai-trench-toggle-${f.fid}`} />
                          Generate trench build-up (excavation · bedding · backfill)
                        </label>
                        {o.trenchOn && (
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {[["W", "Width m", "W"], ["D", "Avg depth m", "D"], ["bed", "Bedding mm", "bed"]].map(([key, label]) => (
                              <label key={key} className="text-[9px] font-bold uppercase text-zinc-500">
                                {label}
                                <Input type="number" value={o[key] ?? ""} onChange={(e) => setOpt(f.fid, { [key]: e.target.value === "" ? null : Number(e.target.value) })}
                                  className="h-6.5 rounded-sm text-[11px] w-20 font-mono mt-0.5" data-testid={`ai-trench-${key}-${f.fid}`} />
                              </label>
                            ))}
                            {c ? (
                              <span className="text-[10px] font-mono text-zinc-500" data-testid={`ai-trench-calc-${f.fid}`}>
                                → Exc <b className="text-[#FF5F15]">{fmtQty(c.exc)}m³</b> · Bed <b>{fmtQty(c.bed)}m³</b> · Backfill <b>{fmtQty(c.backfill)}m³</b>
                              </span>
                            ) : (
                              <span className="text-[10px] text-amber-500">Enter average depth to calculate</span>
                            )}
                            {(f.trench || f.evidence?.detail) && (
                              <span className="w-full text-[9px] text-zinc-500">Source: {f.evidence?.detail || "typical detail values read from the plan"} — edit before approving</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {excluded.length > 0 && (
          <p className="text-[11px] text-amber-500">⚠ {excluded.length} accepted item{excluded.length === 1 ? "" : "s"} still missing a quantity — go back and measure or type them in; they won't be built.</p>
        )}
      </div>

      {/* footer */}
      <div className="shrink-0 px-4 lg:px-6 py-3 border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] flex items-center gap-3">
        <Button variant="outline" onClick={onClose} className="h-10 rounded-sm text-xs font-bold uppercase tracking-wider" data-testid="ai-review-back-btn">
          Back to review
        </Button>
        <div className="flex-1" />
        <span className="text-[11px] text-zinc-500 hidden sm:block">{eligible.length} items · {ratesUsed} rated · {trenchCount} trench build-up{trenchCount === 1 ? "" : "s"}</span>
        <Button onClick={build} disabled={!eligible.length}
          className="h-10 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider px-6" data-testid="ai-build-takeoff">
          <Sparkles className="w-4 h-4 mr-2" /> Build takeoff ({eligible.length})
        </Button>
      </div>
    </div>
  );
}
